import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as THREE from 'three';
import ControlPanel from './ControlPanel';
import SpiderTrack from './SpiderTrack';
import Gallery from './Gallery';
import Viewer3D from './Viewer3D';

// Resultado de la prueba de WebGL, cacheado: crear un contexto por cada cambio
// de vista agota el cupo del navegador y a partir de ahí no concede ninguno.
let hayWebGL = null;

const MembranaViewer = () => {
  // Refs
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const webSystemRef = useRef(null);
  const paintingPlaneRef = useRef(null);
  const animationIdRef = useRef(null);
  const imageDataRef = useRef(null);
  const canvasRef = useRef(null);
  
  // Refs para controles
  const controlsRef = useRef({
    isDragging: false,
    previousMouse: { x: 0, y: 0 },
    rotation: { x: 0, y: 0 },
    distance: 350,
    target: new THREE.Vector3(0, 0, 0),
    keys: {}
  });
  
  // State para UI
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [currentPhase, setCurrentPhase] = useState('Iniciando...');
  const [webCount, setWebCount] = useState(0);
  
  // State para controles
  const [webDensity, setWebDensity] = useState(800);
  const [complexity, setComplexity] = useState('medium');
  const [spiderCount, setSpiderCount] = useState(3);
  const [sizeVariation, setSizeVariation] = useState(0.5);
  
  // MODO DE VISUALIZACIÓN - incluyendo gallery y model3d
  const [viewMode, setViewMode] = useState(
    typeof window !== 'undefined' && window.location.hash === '#spidertrack' ? 'spidertrack' : 'spider2'
  );
  const [sinWebGL, setSinWebGL] = useState(false); // 'painting', 'spider1', 'spider2', 'gallery', 'model3d'
  
  // State para configuración 3D
  const [model3DSettings, setModel3DSettings] = useState({
    autoRotate: false,
    wireframe: false,
    lightIntensity: 5,
    modelPath: '/models/VxsjngEyPlWraU6t6O8a8_tripo_model_c8a8ca1e-07ce-433b-b2e8-896ae2d3024e.glb'
 // Ruta al modelo 3D
  });
  
  // Obtener características pictóricas
  const getPixelFeatures = useCallback((worldX, worldY) => {
    if (!imageDataRef.current || !canvasRef.current) {
      return {
        color: new THREE.Color(0.2, 0.2, 0.2),
        luminance: 0.2,
        gradient: { x: 0, y: 0 },
        contrast: 0.5,
        depth: 0
      };
    }
    
    const worldWidth = 500;
    const worldHeight = (worldWidth * canvasRef.current.height) / canvasRef.current.width;
    
    const u = (worldX + worldWidth / 2) / worldWidth;
    const v = 1 - (worldY + worldHeight / 2) / worldHeight;
    
    const px = Math.floor(Math.max(0, Math.min(canvasRef.current.width - 1, u * canvasRef.current.width)));
    const py = Math.floor(Math.max(0, Math.min(canvasRef.current.height - 1, v * canvasRef.current.height)));
    const index = (py * canvasRef.current.width + px) * 4;
    
    let r = imageDataRef.current.data[index] / 255 || 0;
    let g = imageDataRef.current.data[index + 1] / 255 || 0;
    let b = imageDataRef.current.data[index + 2] / 255 || 0;
    
    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
    
    let gradientX = 0, gradientY = 0;
    if (px > 0 && px < canvasRef.current.width - 1 && py > 0 && py < canvasRef.current.height - 1) {
      const idxLeft = (py * canvasRef.current.width + (px - 1)) * 4;
      const idxRight = (py * canvasRef.current.width + (px + 1)) * 4;
      const idxUp = ((py - 1) * canvasRef.current.width + px) * 4;
      const idxDown = ((py + 1) * canvasRef.current.width + px) * 4;
      
      const lumLeft = (imageDataRef.current.data[idxLeft] + imageDataRef.current.data[idxLeft + 1] + imageDataRef.current.data[idxLeft + 2]) / (3 * 255);
      const lumRight = (imageDataRef.current.data[idxRight] + imageDataRef.current.data[idxRight + 1] + imageDataRef.current.data[idxRight + 2]) / (3 * 255);
      const lumUp = (imageDataRef.current.data[idxUp] + imageDataRef.current.data[idxUp + 1] + imageDataRef.current.data[idxUp + 2]) / (3 * 255);
      const lumDown = (imageDataRef.current.data[idxDown] + imageDataRef.current.data[idxDown + 1] + imageDataRef.current.data[idxDown + 2]) / (3 * 255);
      
      gradientX = lumRight - lumLeft;
      gradientY = lumDown - lumUp;
    }
    
    const contrast = Math.sqrt(gradientX * gradientX + gradientY * gradientY);
    const depth = (luminance - 0.5) * 30;
    
    const desaturation = 0.2;
    r = luminance + (r - luminance) * (1 - desaturation);
    g = luminance + (g - luminance) * (1 - desaturation);
    b = luminance + (b - luminance) * (1 - desaturation);
    
    const contrastBoost = 1.5;
    const brightness = 1.2;
    
    r = ((r - 0.5) * contrastBoost + 0.5) * brightness;
    g = ((g - 0.5) * contrastBoost + 0.5) * brightness;
    b = ((b - 0.5) * contrastBoost + 0.5) * brightness;
    
    return {
      color: new THREE.Color(
        Math.max(0, Math.min(1, r)),
        Math.max(0, Math.min(1, g)),
        Math.max(0, Math.min(1, b))
      ),
      luminance,
      gradient: { x: gradientX, y: gradientY },
      contrast,
      depth
    };
  }, []);
  
  // Crear plano con la pintura
  const createPaintingPlane = useCallback(() => {
    if (!canvasRef.current) return null;
    
    const worldWidth = 350;
    const worldHeight = (worldWidth * canvasRef.current.height) / canvasRef.current.width;
    
    const geometry = new THREE.PlaneGeometry(worldWidth, worldHeight, 1, 1);
    const texture = new THREE.CanvasTexture(canvasRef.current);
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    
    const material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: false,
      side: THREE.DoubleSide
    });
    
    const plane = new THREE.Mesh(geometry, material);
    plane.position.z = 0;
    
    return plane;
  }, []);
  
  // Función catenaria
  const createCatenaryLine = useCallback((start, end, tension = 1) => {
    const points = [];
    const segments = 30;
    
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const dz = end.z - start.z;
    const length = Math.sqrt(dx * dx + dy * dy);
    
    const a = length / (2 * Math.max(0.1, tension));
    
    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const x = start.x + dx * t;
      const z = start.z + dz * t;
      
      const localX = (t - 0.5) * length;
      const sag = a * (Math.cosh(localX / a) - Math.cosh(length / (2 * a)));
      const y = start.y + dy * t + sag * 0.1;
      
      points.push(new THREE.Vector3(x, y, z));
    }
    
    return points;
  }, []);
  
  // CREAR TELARAÑA TIPO 2 - Estilo documento
  const createType2Web = useCallback((centerX, centerY, size, uniqueSeed = Math.random()) => {
    const group = new THREE.Group();
    const center = new THREE.Vector3(centerX, centerY, 0);
    group.position.copy(center);
    
    const seededRandom = (seed) => {
      const x = Math.sin(seed) * 10000;
      return x - Math.floor(x);
    };
    
    const features = getPixelFeatures(centerX, centerY);
    
    const materialDensity = 1 - features.luminance;
    const fiberOrientation = Math.atan2(features.gradient.y, features.gradient.x);
    const structuralTension = features.contrast * 2 + 1;
    
    group.position.z = features.depth;
    
    const webParams = {
      radialCount: Math.floor(8 + materialDensity * 16),
      spiralTurns: 3 + materialDensity * 4,
      baseAngle: fiberOrientation,
      tension: structuralTension,
      irregularity: seededRandom(uniqueSeed * 2) * 0.3
    };
    
    // RADIOS
    for (let i = 0; i < webParams.radialCount; i++) {
      const angle = webParams.baseAngle + (i * 2 * Math.PI / webParams.radialCount);
      
      const geometry = new THREE.BufferGeometry();
      const positions = [];
      const colors = [];
      
      const maxRadius = size * (0.8 + features.contrast * 0.4);
      
      const endPoint = new THREE.Vector3(
        Math.cos(angle) * maxRadius,
        Math.sin(angle) * maxRadius,
        (seededRandom(uniqueSeed * 100 + i) - 0.5) * 10
      );
      
      const catenaryPoints = createCatenaryLine(
        new THREE.Vector3(0, 0, 0),
        endPoint,
        webParams.tension
      );
      
      catenaryPoints.forEach(point => {
        positions.push(point.x, point.y, point.z);
        const worldX = centerX + point.x;
        const worldY = centerY + point.y;
        const pointFeatures = getPixelFeatures(worldX, worldY);
        colors.push(pointFeatures.color.r, pointFeatures.color.g, pointFeatures.color.b);
      });
      
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
      
      const material = new THREE.LineBasicMaterial({
        vertexColors: true,
        opacity: 0.7,
        transparent: true,
        linewidth: 1.5
      });
      
      const line = new THREE.Line(geometry, material);
      group.add(line);
    }
    
    // ESPIRAL
    if (complexity !== 'simple') {
      const spiralGeometry = new THREE.BufferGeometry();
      const spiralPositions = [];
      const spiralColors = [];
      
      const a = 5 * materialDensity;
      const b = size / (2 * Math.PI * webParams.spiralTurns);
      
      const segments = Math.floor(100 * webParams.spiralTurns);
      
      for (let i = 0; i <= segments; i++) {
        const t = i / segments;
        const theta = t * Math.PI * 2 * webParams.spiralTurns;
        
        const r = a + b * theta;
        const wobble = Math.sin(theta * 5) * 2 * webParams.irregularity;
        
        const x = (r + wobble) * Math.cos(theta + webParams.baseAngle);
        const y = (r + wobble) * Math.sin(theta + webParams.baseAngle);
        const z = Math.sin(theta * 2) * 3;
        
        if (Math.sqrt(x*x + y*y) <= size) {
          spiralPositions.push(x, y, z);
          
          const worldX = centerX + x;
          const worldY = centerY + y;
          const pointFeatures = getPixelFeatures(worldX, worldY);
          spiralColors.push(pointFeatures.color.r, pointFeatures.color.g, pointFeatures.color.b);
        }
      }
      
      if (spiralPositions.length > 0) {
        spiralGeometry.setAttribute('position', new THREE.Float32BufferAttribute(spiralPositions, 3));
        spiralGeometry.setAttribute('color', new THREE.Float32BufferAttribute(spiralColors, 3));
        
        const spiralMaterial = new THREE.LineBasicMaterial({
          vertexColors: true,
          opacity: 0.5,
          transparent: true,
          linewidth: 1
        });
        
        const spiral = new THREE.Line(spiralGeometry, spiralMaterial);
        group.add(spiral);
      }
    }
    
    group.userData = { features, webParams };
    
    return group;
  }, [getPixelFeatures, createCatenaryLine, complexity]);
  
  // CREAR TELARAÑA TIPO 1 - Con más variación
  const createType1Web = useCallback((centerX, centerY, size, uniqueSeed = Math.random(), isExternal = false) => {
    const group = new THREE.Group();
    group.position.set(centerX, centerY, 0);
    
    const seededRandom = (seed) => {
      const x = Math.sin(seed) * 10000;
      return x - Math.floor(x);
    };
    
    const features = getPixelFeatures(centerX, centerY);
    
    const sizeMultiplier = 1 + (seededRandom(uniqueSeed * 100) - 0.5) * sizeVariation * 2;
    const adjustedSize = size * sizeMultiplier * (0.8 + seededRandom(uniqueSeed * 101) * 0.6);
    
    const materialDensity = 1 - features.luminance;
    const fiberOrientation = Math.atan2(features.gradient.y, features.gradient.x) + (seededRandom(uniqueSeed * 102) - 0.5) * Math.PI;
    const structuralTension = features.contrast * 2 + 1 + (seededRandom(uniqueSeed * 103) - 0.5);
    
    group.position.z = features.depth + (seededRandom(uniqueSeed * 104) - 0.5) * 20;
    
    let numRadials, numRings, hasSpiral;
    switch(complexity) {
      case 'simple':
        numRadials = 12 + Math.floor(seededRandom(uniqueSeed * 105) * 4);
        numRings = 6 + Math.floor(seededRandom(uniqueSeed * 106) * 2);
        hasSpiral = seededRandom(uniqueSeed * 107) > 0.5;
        break;
      case 'medium':
        numRadials = 16 + Math.floor(seededRandom(uniqueSeed * 105) * 8);
        numRings = 10 + Math.floor(seededRandom(uniqueSeed * 106) * 4);
        hasSpiral = true;
        break;
      case 'complex':
        numRadials = 24 + Math.floor(seededRandom(uniqueSeed * 105) * 12);
        numRings = 14 + Math.floor(seededRandom(uniqueSeed * 106) * 6);
        hasSpiral = true;
        break;
      default:
        numRadials = 20;
        numRings = 10;
        hasSpiral = true;
    }
    
    // Hilos externos
    if (isExternal || seededRandom(uniqueSeed * 200) > 0.7) {
      const numExternalThreads = 2 + Math.floor(seededRandom(uniqueSeed * 201) * 3);
      for (let i = 0; i < numExternalThreads; i++) {
        const angle = seededRandom(uniqueSeed * 202 + i) * Math.PI * 2;
        const externalDistance = adjustedSize * (2 + seededRandom(uniqueSeed * 203 + i) * 3);
        
        const geometry = new THREE.BufferGeometry();
        const positions = [];
        const colors = [];
        
        const startPoint = new THREE.Vector3(
          Math.cos(angle) * externalDistance,
          Math.sin(angle) * externalDistance,
          (seededRandom(uniqueSeed * 204 + i) - 0.5) * 30
        );
        
        const endPoint = new THREE.Vector3(
          Math.cos(angle + Math.PI) * adjustedSize * 0.3,
          Math.sin(angle + Math.PI) * adjustedSize * 0.3,
          (seededRandom(uniqueSeed * 205 + i) - 0.5) * 10
        );
        
        const catenaryPoints = createCatenaryLine(
          startPoint,
          endPoint,
          structuralTension * 0.5
        );
        
        catenaryPoints.forEach(point => {
          positions.push(point.x, point.y, point.z);
          colors.push(0.3, 0.25, 0.2);
        });
        
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
        
        const material = new THREE.LineBasicMaterial({
          vertexColors: true,
          opacity: 0.4,
          transparent: true,
          linewidth: 1
        });
        
        const line = new THREE.Line(geometry, material);
        group.add(line);
      }
    }
    
    // Radios
    for (let i = 0; i < numRadials; i++) {
      const angleVariation = (seededRandom(uniqueSeed * 300 + i) - 0.5) * 0.3;
      const angle = fiberOrientation + (i * 2 * Math.PI / numRadials) + angleVariation;
      
      const geometry = new THREE.BufferGeometry();
      const positions = [];
      const colors = [];
      
      const radiusVariation = 0.7 + seededRandom(uniqueSeed * 301 + i) * 0.5;
      const maxRadius = adjustedSize * radiusVariation;
      
      const endPoint = new THREE.Vector3(
        Math.cos(angle) * maxRadius,
        Math.sin(angle) * maxRadius,
        (seededRandom(uniqueSeed * 302 + i) - 0.5) * 15
      );
      
      const catenaryPoints = createCatenaryLine(
        new THREE.Vector3(0, 0, 0),
        endPoint,
        structuralTension
      );
      
      catenaryPoints.forEach(point => {
        positions.push(point.x, point.y, point.z);
        const worldX = centerX + point.x;
        const worldY = centerY + point.y;
        const pointFeatures = getPixelFeatures(worldX, worldY);
        colors.push(
          pointFeatures.color.r,
          pointFeatures.color.g,
          pointFeatures.color.b
        );
      });
      
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
      
      const material = new THREE.LineBasicMaterial({
        vertexColors: true,
        opacity: 0.9,
        transparent: true,
        linewidth: 2
      });
      
      const line = new THREE.Line(geometry, material);
      group.add(line);
    }
    
    // Anillos
    for (let i = 0; i < numRings; i++) {
      const ringRadius = adjustedSize * (0.1 + (i / numRings) * 0.85);
      
      const geometry = new THREE.BufferGeometry();
      const positions = [];
      const colors = [];
      
      const segments = 48 + Math.floor(seededRandom(uniqueSeed * 400 + i) * 32);
      
      for (let j = 0; j <= segments; j++) {
        const angle = (j / segments) * Math.PI * 2;
        const wobbleFreq = 3 + Math.floor(seededRandom(uniqueSeed * 401 + i) * 4);
        const wobbleAmp = 2 + seededRandom(uniqueSeed * 402 + i) * 4;
        const wobble = Math.sin(angle * wobbleFreq + i) * wobbleAmp;
        
        const radiusVariation = 1 + (seededRandom(uniqueSeed * 403 + i * segments + j) - 0.5) * 0.1;
        const x = Math.cos(angle) * (ringRadius + wobble) * radiusVariation;
        const y = Math.sin(angle) * (ringRadius + wobble) * radiusVariation;
        const z = Math.sin(angle * 3 + i * 0.5) * 3 + (seededRandom(uniqueSeed * 404 + j) - 0.5) * 5;
        
        positions.push(x, y, z);
        
        const worldX = centerX + x;
        const worldY = centerY + y;
        const pointFeatures = getPixelFeatures(worldX, worldY);
        colors.push(
          pointFeatures.color.r * 0.95,
          pointFeatures.color.g * 0.95,
          pointFeatures.color.b * 0.95
        );
      }
      
      if (positions.length > 0) {
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
        
        const material = new THREE.LineBasicMaterial({
          vertexColors: true,
          opacity: 0.8 - i * 0.02,
          transparent: true,
          linewidth: 1.5
        });
        
        const line = new THREE.Line(geometry, material);
        group.add(line);
      }
    }
    
    // Espiral
    if (hasSpiral) {
      const spiralGeometry = new THREE.BufferGeometry();
      const spiralPositions = [];
      const spiralColors = [];
      
      const spiralTurns = 3 + Math.floor(materialDensity * 4) + Math.floor(seededRandom(uniqueSeed * 500) * 3);
      const segments = 80 * spiralTurns;
      
      const spiralTightness = 0.8 + seededRandom(uniqueSeed * 501) * 0.4;
      
      for (let i = 0; i <= segments; i++) {
        const t = i / segments;
        const theta = t * Math.PI * 2 * spiralTurns;
        const r = 5 + (adjustedSize * 0.85) * (theta / (Math.PI * 2 * spiralTurns)) * spiralTightness;
        
        if (r <= adjustedSize * 0.9) {
          const wobble = Math.sin(theta * 7) * (1 + seededRandom(uniqueSeed * 502 + i) * 2);
          const x = (r + wobble) * Math.cos(theta + fiberOrientation);
          const y = (r + wobble) * Math.sin(theta + fiberOrientation);
          const z = Math.sin(theta * 2) * 2 + (seededRandom(uniqueSeed * 503 + i) - 0.5) * 3;
          
          spiralPositions.push(x, y, z);
          
          const worldX = centerX + x;
          const worldY = centerY + y;
          const pointFeatures = getPixelFeatures(worldX, worldY);
          spiralColors.push(
            pointFeatures.color.r * 0.85,
            pointFeatures.color.g * 0.85,
            pointFeatures.color.b * 0.85
          );
        }
      }
      
      if (spiralPositions.length > 0) {
        spiralGeometry.setAttribute('position', new THREE.Float32BufferAttribute(spiralPositions, 3));
        spiralGeometry.setAttribute('color', new THREE.Float32BufferAttribute(spiralColors, 3));
        
        const spiralMaterial = new THREE.LineBasicMaterial({
          vertexColors: true,
          opacity: 0.7,
          transparent: true,
          linewidth: 1
        });
        
        const spiral = new THREE.Line(spiralGeometry, spiralMaterial);
        group.add(spiral);
      }
    }
    
    group.userData = { 
      features,
      seed: uniqueSeed,
      centerX,
      centerY,
      size: adjustedSize,
      depth: features.depth
    };
    
    return group;
  }, [getPixelFeatures, createCatenaryLine, complexity, sizeVariation]);
  
  // GENERAR SISTEMA DE TELARAÑAS
  const generateWebs = useCallback(() => {
    if (!canvasRef.current) return;
    
    setCurrentPhase('Analizando composición pictórica...');
    
    // Limpiar sistema anterior
    if (webSystemRef.current) {
      webSystemRef.current.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });
      sceneRef.current.remove(webSystemRef.current);
    }
    
    if (paintingPlaneRef.current) {
      sceneRef.current.remove(paintingPlaneRef.current);
    }
    
    if (viewMode === 'painting') {
      // Solo pintura
      paintingPlaneRef.current = createPaintingPlane();
      if (paintingPlaneRef.current) {
        sceneRef.current.add(paintingPlaneRef.current);
      }
      setWebCount(0);
    } else if (viewMode === 'spider1' || viewMode === 'spider2') {
      // Crear telarañas
      const webSystem = new THREE.Group();
      webSystemRef.current = webSystem;
      
      const worldWidth = 500;
      const worldHeight = (worldWidth * canvasRef.current.height) / canvasRef.current.width;
      
      const totalWebs = Math.floor(webDensity);
      const gridSize = Math.floor(Math.sqrt(totalWebs * 0.6));
      const additionalWebs = Math.floor(totalWebs * 0.4);
      
      const cellWidth = worldWidth / gridSize;
      const cellHeight = worldHeight / gridSize;
      
      let webCounter = 0;
      const webData = [];
      
      // Grid principal
      for (let row = -1; row <= gridSize; row++) {
        for (let col = -1; col <= gridSize; col++) {
          const baseX = (col - gridSize / 2 + 0.5) * cellWidth;
          const baseY = -(row - gridSize / 2 + 0.5) * cellHeight;
          
          const regionFeatures = getPixelFeatures(baseX, baseY);
          const placementProbability = 1 - regionFeatures.luminance * 0.3;
          
          if (Math.random() < placementProbability) {
            const offsetX = (Math.random() - 0.5) * cellWidth * 0.4;
            const offsetY = (Math.random() - 0.5) * cellHeight * 0.4;
            
            const x = baseX + offsetX;
            const y = baseY + offsetY;
            
            const size = cellWidth * (0.8 + regionFeatures.contrast * 0.6);
            const seed = Math.random();
            const isExternal = Math.abs(x) > worldWidth * 0.35 || Math.abs(y) > worldHeight * 0.35;
            
            webData.push({ x, y, size, seed, isExternal });
            webCounter++;
          }
        }
      }
      
      // Telarañas adicionales
      for (let i = 0; i < additionalWebs; i++) {
        const x = (Math.random() - 0.5) * worldWidth * 1.2;
        const y = (Math.random() - 0.5) * worldHeight * 1.2;
        
        const features = getPixelFeatures(x, y);
        
        if (features.contrast > 0.2) {
          const size = cellWidth * (0.6 + features.contrast * 0.8);
          const seed = Math.random();
          const isExternal = Math.abs(x) > worldWidth * 0.35 || Math.abs(y) > worldHeight * 0.35;
          
          webData.push({ x, y, size, seed, isExternal });
          webCounter++;
        }
      }
      
      // Crear todas las telarañas según el tipo
      webData.forEach(data => {
        let web;
        if (viewMode === 'spider1') {
          web = createType1Web(data.x, data.y, data.size, data.seed, data.isExternal);
        } else {
          web = createType2Web(data.x, data.y, data.size, data.seed);
        }
        webSystem.add(web);
      });
      
      sceneRef.current.add(webSystem);
      setWebCount(webCounter);
    }
    
    setCurrentPhase('Sistema generado');
    setIsLoading(false);
  }, [createType1Web, createType2Web, createPaintingPlane, webDensity, getPixelFeatures, viewMode]);
  
  // Procesar imagen
  const processImage = useCallback(async () => {
    try {
      setCurrentPhase('Cargando obra pictórica...');
      setLoadingProgress(10);
      
      const img = await new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = '/images/Velazquez-las_hilanderas.jpg';
      });
      
      setCurrentPhase('Extrayendo características pictóricas...');
      setLoadingProgress(30);
      
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      canvasRef.current = canvas;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      imageDataRef.current = ctx.getImageData(0, 0, canvas.width, canvas.height);
      
      setLoadingProgress(50);
      
      generateWebs();
      
      setLoadingProgress(100);
      
    } catch (error) {
      console.error('Error:', error);
      setCurrentPhase('Error');
      setIsLoading(false);
    }
  }, [generateWebs]);
  
  // Inicializar Three.js
  const initThreeJS = useCallback(() => {
    if (!mountRef.current) return;
    
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);
    scene.fog = new THREE.Fog(0x000000, 100, 800);
    sceneRef.current = scene;
    
    const camera = new THREE.PerspectiveCamera(
      60,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    camera.position.set(0, 0, 350);
    cameraRef.current = camera;
    
    // Se comprueba antes de construir nada: si no hay WebGL, three.js llena la
    // consola de errores propios aunque el fallo esté capturado. La prueba se
    // hace UNA vez y se suelta el contexto: el navegador tiene un cupo de
    // contextos vivos y una prueba por cada cambio de vista lo agota.
    if (hayWebGL === null) {
      const prueba = document.createElement('canvas');
      const ctx = prueba.getContext('webgl2') || prueba.getContext('webgl');
      hayWebGL = !!ctx;
      if (ctx) ctx.getExtension('WEBGL_lose_context')?.loseContext();
    }
    if (!hayWebGL) {
      console.warn('Sin WebGL: el navegador no da contexto acelerado');
      setSinWebGL(true);
      return;
    }

    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: false,
        powerPreference: "high-performance",
        preserveDrawingBuffer: true
      });
    } catch (err) {
      // Sin aceleración por hardware no hay WebGL, pero SpiderTrack es lienzo
      // 2D y sí funciona, así que se va allí en vez de tumbar la aplicación.
      console.warn('Sin WebGL:', err.message);
      setSinWebGL(true);
      return;
    }
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    rendererRef.current = renderer;
    mountRef.current.appendChild(renderer.domElement);
    
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
    scene.add(ambientLight);
    
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.2);
    directionalLight.position.set(50, 50, 100);
    scene.add(directionalLight);
  }, []);
  
  // Actualizar cámara
  const updateCamera = useCallback(() => {
    if (!cameraRef.current) return;
    
    const controls = controlsRef.current;
    const camera = cameraRef.current;
    
    const x = controls.target.x + controls.distance * Math.sin(controls.rotation.y) * Math.cos(controls.rotation.x);
    const y = controls.target.y + controls.distance * Math.sin(controls.rotation.x);
    const z = controls.target.z + controls.distance * Math.cos(controls.rotation.y) * Math.cos(controls.rotation.x);
    
    camera.position.set(x, y, z);
    camera.lookAt(controls.target);
  }, []);
  
  // Procesar entrada de teclado
  const processKeyboardInput = useCallback(() => {
    const controls = controlsRef.current;
    const keys = controls.keys;
    
    // Velocidad de movimiento
    const rotSpeed = keys['Shift'] ? 0.05 : 0.02;
    const panSpeed = keys['Shift'] ? 8 : 4;
    const zoomSpeed = keys['Shift'] ? 20 : 10;
    
    // Rotación con flechas
    if (keys['ArrowLeft']) {
      controls.rotation.y -= rotSpeed;
    }
    if (keys['ArrowRight']) {
      controls.rotation.y += rotSpeed;
    }
    if (keys['ArrowUp']) {
      controls.rotation.x = Math.max(-Math.PI/2 + 0.1, controls.rotation.x - rotSpeed);
    }
    if (keys['ArrowDown']) {
      controls.rotation.x = Math.min(Math.PI/2 - 0.1, controls.rotation.x + rotSpeed);
    }
    
    // Pan con WASD
    if (keys['w'] || keys['W']) {
      controls.target.y += panSpeed;
    }
    if (keys['s'] || keys['S']) {
      controls.target.y -= panSpeed;
    }
    if (keys['a'] || keys['A']) {
      controls.target.x -= panSpeed;
    }
    if (keys['d'] || keys['D']) {
      controls.target.x += panSpeed;
    }
    
    // Zoom con Q/E o +/-
    if (keys['q'] || keys['Q'] || keys['-'] || keys['_']) {
      controls.distance = Math.min(1000, controls.distance + zoomSpeed);
    }
    if (keys['e'] || keys['E'] || keys['+'] || keys['=']) {
      controls.distance = Math.max(10, controls.distance - zoomSpeed);
    }
    
    // Elevación con Z/X
    if (keys['z'] || keys['Z']) {
      controls.target.z -= panSpeed;
    }
    if (keys['x'] || keys['X']) {
      controls.target.z += panSpeed;
    }
    
    // Reset con R
    if (keys['r'] || keys['R']) {
      controls.rotation.x = 0;
      controls.rotation.y = 0;
      controls.distance = 350;
      controls.target.set(0, 0, 0);
      keys['r'] = false; // Solo una vez por pulsación
      keys['R'] = false;
    }
    
    // Actualizar cámara si hay teclas presionadas
    if (Object.values(keys).some(v => v)) {
      updateCamera();
    }
  }, [updateCamera]);
  
  // Mouse controls
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    controlsRef.current.isDragging = true;
    controlsRef.current.previousMouse = { x: e.clientX, y: e.clientY };
  }, []);
  
  const handleMouseMove = useCallback((e) => {
    const controls = controlsRef.current;
    
    if (controls.isDragging) {
      const deltaX = e.clientX - controls.previousMouse.x;
      const deltaY = e.clientY - controls.previousMouse.y;
      
      controls.rotation.y += deltaX * 0.01;
      controls.rotation.x = Math.max(-Math.PI/2 + 0.1, Math.min(Math.PI/2 - 0.1, controls.rotation.x + deltaY * 0.01));
      
      controls.previousMouse = { x: e.clientX, y: e.clientY };
      updateCamera();
    }
  }, [updateCamera]);
  
  const handleMouseUp = useCallback(() => {
    controlsRef.current.isDragging = false;
  }, []);
  
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const controls = controlsRef.current;
    controls.distance = Math.max(10, Math.min(1000, controls.distance + e.deltaY * 0.1));
    updateCamera();
  }, [updateCamera]);
  
  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
  }, []);
  
  // Keyboard controls
  const handleKeyDown = useCallback((e) => {
    // Prevenir scroll de página con flechas SOLO si no estamos en galería o modelo 3D
    if (viewMode !== 'gallery' && viewMode !== 'model3d' && viewMode !== 'spidertrack' && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' '].includes(e.key)) {
      e.preventDefault();
    }
    // ESC para volver del modelo 3D
    if (e.key === 'Escape' && viewMode === 'model3d') {
      setViewMode('spider2');
    }
    controlsRef.current.keys[e.key] = true;
  }, [viewMode]);
  
  const handleKeyUp = useCallback((e) => {
    controlsRef.current.keys[e.key] = false;
  }, []);
  
  // Función para exportar imagen
  const exportImage = useCallback((format = 'png', quality = 1.0) => {
    if (!rendererRef.current || !sceneRef.current || !cameraRef.current) {
      console.error('No hay escena para exportar');
      return;
    }
    
    rendererRef.current.render(sceneRef.current, cameraRef.current);
    
    const canvas = rendererRef.current.domElement;
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    link.download = `membrana-velazquez-${timestamp}.${format}`;
    
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
    }, `image/${format}`, quality);
  }, []);
  
  // Animación principal
  const animate = useCallback(() => {
    if (!rendererRef.current || !sceneRef.current || !cameraRef.current || viewMode === 'gallery' || viewMode === 'model3d') return;
    
    animationIdRef.current = requestAnimationFrame(animate);
    
    // Procesar teclado
    processKeyboardInput();
    
    // Física de balanceo sutil
    if (webSystemRef.current && !controlsRef.current.isDragging && viewMode !== 'painting') {
      const time = Date.now() * 0.00005;
      
      webSystemRef.current.children.forEach((web, i) => {
        const originalZ = web.userData?.features?.depth || 0;
        const tension = web.userData?.features?.contrast || 1;
        
        web.position.z = originalZ + Math.sin(time + i * 0.3) * 2 * (1 - tension);
        web.rotation.x = Math.sin(time + i * 0.5) * 0.005 * (1 - tension);
        web.rotation.y = Math.cos(time + i * 0.7) * 0.005 * (1 - tension);
      });
    }
    
    rendererRef.current.render(sceneRef.current, cameraRef.current);
  }, [viewMode, processKeyboardInput]);
  
  // Resize
  const handleResize = useCallback(() => {
    if (!cameraRef.current || !rendererRef.current) return;
    
    cameraRef.current.aspect = window.innerWidth / window.innerHeight;
    cameraRef.current.updateProjectionMatrix();
    rendererRef.current.setSize(window.innerWidth, window.innerHeight);
  }, []);
  
  // Manejadores para controles
  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
  }, []);
  
  const handleDensityChange = useCallback((value) => {
    setWebDensity(value);
  }, []);
  
  const handleComplexityChange = useCallback((value) => {
    setComplexity(value);
  }, []);
  
  const handleSpiderCountChange = useCallback((value) => {
    setSpiderCount(value);
  }, []);
  
  const handleSizeVariationChange = useCallback((value) => {
    setSizeVariation(value);
  }, []);
  
  const handleModel3DSettingsChange = useCallback((settings) => {
    setModel3DSettings(prev => ({
      ...prev,
      ...settings
    }));
  }, []);
  
  // Aplicar cambios de modo de vista
  useEffect(() => {
    if (imageDataRef.current && !isLoading && viewMode !== 'gallery' && viewMode !== 'model3d' && viewMode !== 'spidertrack') {
      generateWebs();
    }
  }, [viewMode, generateWebs, isLoading]);
  
  // Regenerar cuando cambian controles
  useEffect(() => {
    if (imageDataRef.current && !isLoading && viewMode !== 'gallery' && viewMode !== 'model3d' && viewMode !== 'spidertrack') {
      generateWebs();
    }
  }, [webDensity, complexity, spiderCount, sizeVariation, generateWebs, isLoading, viewMode]);
  
  // Efecto principal
  useEffect(() => {
    if (viewMode !== 'gallery' && viewMode !== 'model3d' && viewMode !== 'spidertrack') {
      initThreeJS();
      animate();
      
      const mount = mountRef.current;
      if (mount) {
        mount.addEventListener('mousedown', handleMouseDown);
        mount.addEventListener('contextmenu', handleContextMenu);
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        mount.addEventListener('wheel', handleWheel, { passive: false });
        window.addEventListener('resize', handleResize);
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
      }
      
      processImage();
      
      return () => {
        if (animationIdRef.current) {
          cancelAnimationFrame(animationIdRef.current);
        }
        if (mount) {
          mount.removeEventListener('mousedown', handleMouseDown);
          mount.removeEventListener('wheel', handleWheel);
          mount.removeEventListener('contextmenu', handleContextMenu);
        }
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('keyup', handleKeyUp);
        
        // dispose() suelta las texturas pero no el contexto: sin
        // forceContextLoss se queda vivo y, al cabo de unos cambios de vista,
        // el navegador deja de conceder contextos nuevos.
        if (rendererRef.current) {
          const lienzo = rendererRef.current.domElement;
          if (lienzo && lienzo.parentNode) lienzo.parentNode.removeChild(lienzo);
          rendererRef.current.forceContextLoss();
          rendererRef.current.dispose();
          rendererRef.current = null;
        }
      };
    }
  }, [viewMode]);
  
  // Renderizado condicional según el modo
  
  // Sin WebGL: la parte 3D no puede arrancar, pero SpiderTrack sí
  if (sinWebGL && viewMode !== 'spidertrack') {
    return (
      <div style={{
        position: 'fixed', inset: 0, background: '#000', color: 'rgba(200,180,150,0.85)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: '18px', textAlign: 'center', padding: '40px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}>
        <div style={{ fontSize: '12px', letterSpacing: '0.16em', textTransform: 'uppercase' }}>
          Este navegador no da WebGL
        </div>
        <div style={{ fontSize: '11px', lineHeight: 1.7, maxWidth: '560px', color: 'rgba(200,180,150,0.55)' }}>
          Las vistas de telarañas y el modelo 3D necesitan aceleración por hardware. Suele
          arreglarse activando «Usar aceleración por hardware» en los ajustes del navegador y
          reiniciándolo. SpiderTrack no la necesita, va en lienzo 2D.
        </div>
        <button
          style={{
            padding: '11px 22px', background: 'transparent',
            border: '1px solid rgba(200,180,150,0.35)', borderRadius: '6px',
            color: 'rgba(200,180,150,0.9)', fontSize: '10px', fontWeight: 600,
            letterSpacing: '0.12em', textTransform: 'uppercase', cursor: 'pointer',
            fontFamily: 'inherit',
          }}
          onClick={() => { hayWebGL = null; setSinWebGL(false); }}
        >
          Reintentar
        </button>
        <button
          style={{
            padding: '11px 22px', background: 'transparent',
            border: '1px solid rgba(200,180,150,0.35)', borderRadius: '6px',
            color: 'rgba(200,180,150,0.9)', fontSize: '10px', fontWeight: 600,
            letterSpacing: '0.12em', textTransform: 'uppercase', cursor: 'pointer',
            fontFamily: 'inherit',
          }}
          onClick={() => setViewMode('spidertrack')}
        >
          Abrir SpiderTrack
        </button>
      </div>
    );
  }

  // Vista SpiderTrack, a pantalla completa
  if (viewMode === 'spidertrack') {
    return <SpiderTrack onSalir={() => setViewMode('spider2')} />;
  }

  // Vista de Galería
  if (viewMode === 'gallery') {
    return (
      <div className="membrana-viewer">
        <div style={{
          width: '100vw',
          height: '100vh',
          background: 'radial-gradient(circle at center, rgba(200, 180, 150, 0.02) 0%, #000000 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative'
        }}>
          <Gallery />
          
          {/* Botón para volver al viewer 3D */}
          <button
            style={{
              position: 'fixed',
              top: '20px',
              right: '20px',
              padding: '10px 20px',
              background: 'rgba(10, 10, 10, 0.9)',
              border: '1px solid rgba(200, 180, 150, 0.3)',
              borderRadius: '6px',
              color: 'rgba(200, 180, 150, 0.9)',
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontFamily: 'inherit',
              zIndex: 100,
              backdropFilter: 'blur(10px)',
            }}
            onClick={() => setViewMode('spider2')}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(200, 180, 150, 0.15)';
              e.target.style.border = '1px solid rgba(200, 180, 150, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(10, 10, 10, 0.9)';
              e.target.style.border = '1px solid rgba(200, 180, 150, 0.3)';
            }}
          >
            ← Volver al Viewer 3D
          </button>
        </div>
      </div>
    );
  }
  
  // Vista de Modelo 3D
  if (viewMode === 'model3d') {
    return (
      <div className="membrana-viewer">
        <Viewer3D 
          modelUrl={model3DSettings.modelPath}
          settings={model3DSettings}
        />
        <ControlPanel
          webCount={webCount}
          webDensity={webDensity}
          complexity={complexity}
          onDensityChange={handleDensityChange}
          onComplexityChange={handleComplexityChange}
          isLoading={false}
          spiderCount={spiderCount}
          onSpiderCountChange={handleSpiderCountChange}
          sizeVariation={sizeVariation}
          onSizeVariationChange={handleSizeVariationChange}
          onExportImage={exportImage}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          model3DSettings={model3DSettings}
          onModel3DSettingsChange={handleModel3DSettingsChange}
        />
      </div>
    );
  }
  
  // Vista normal del viewer 3D (arañas y pintura)
  return (
    <div className="membrana-viewer">
      {isLoading && (
        <div className="loading-screen">
          <div className="loading-content">
            <div className="loading-title">MEMBRANA Vol. 3</div>
            <div className="loading-subtitle">TEJIENDO EL FUTURO</div>
            <div className="loading-description">{currentPhase}</div>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${loadingProgress}%` }} />
            </div>
            <div className="progress-text">{loadingProgress}%</div>
          </div>
        </div>
      )}
      
      <div ref={mountRef} className="viewer-container" />
      
      <ControlPanel
        webCount={webCount}
        webDensity={webDensity}
        complexity={complexity}
        onDensityChange={handleDensityChange}
        onComplexityChange={handleComplexityChange}
        isLoading={isLoading}
        spiderCount={spiderCount}
        onSpiderCountChange={handleSpiderCountChange}
        sizeVariation={sizeVariation}
        onSizeVariationChange={handleSizeVariationChange}
        onExportImage={exportImage}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        model3DSettings={model3DSettings}
        onModel3DSettingsChange={handleModel3DSettingsChange}
      />
      
      {/* Indicador de controles */}
      <div style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        padding: '12px',
        background: 'rgba(10, 10, 10, 0.85)',
        borderRadius: '8px',
        border: '1px solid rgba(200, 180, 150, 0.2)',
        color: 'rgba(200, 180, 150, 0.7)',
        fontSize: '10px',
        fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
        lineHeight: '1.4',
        backdropFilter: 'blur(10px)',
      }}>
        <div style={{ marginBottom: '4px', fontWeight: 'bold', color: 'rgba(200, 180, 150, 0.9)' }}>
          Controles de navegación:
        </div>
        <div>Arrastrar: Rotar</div>
        <div>Scroll: Zoom</div>
        <div>Flechas: Rotar vista</div>
        <div>WASD: Mover escena</div>
        <div>Q/E o +/-: Zoom</div>
        <div>Z/X: Elevar/Bajar</div>
        <div>R: Reset vista</div>
        <div>Shift: Movimiento rápido</div>
      </div>
    </div>
  );
};

export default MembranaViewer;